import { ReactElement, ReactNode } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import { NumberFormatProps } from 'react-number-format'
import { isUndefined, round, toNumber } from 'lodash-es'
import { InputAdornment, OutlinedTextFieldProps, TextField } from '@mui/material'
import FormControl from '../FormControl'
import NumberInput from '../NumberInput'
import DecimalScaleWarningIndicator from './components/DecimalScaleWarningIndicator'

export interface CoordinateNumberInputProps
  extends Omit<OutlinedTextFieldProps, 'variant' | 'InputProps' | 'type'> {
  name: string
  placeholder?: string
  label?: string | ReactNode
  formControlLabel?: string | ReactNode
  decimalSeparator?: NumberFormatProps['decimalSeparator']
  decimalScale?: NumberFormatProps['decimalScale']
  InputProps?: NumberFormatProps
  unit?: string
  onValueChange?: (val: number | null) => void
}

/**
 * This component will set field either to a number or null, so handle the null case
 * @param param0
 * @returns
 */
const CoordinateNumberInput = ({
  name,
  rows = 1,
  multiline = false,
  autoFocus = false,
  disabled = false,
  label = '',
  formControlLabel = '',
  unit,
  decimalSeparator = ',',
  decimalScale,
  InputProps = {},
  onValueChange,
  ...textFieldProps
}: CoordinateNumberInputProps): ReactElement => {
  const { control } = useFormContext()
  const {
    field: { onChange, onBlur, name: fieldName, ref },
    fieldState: { error },
  } = useController({ name, control })

  const value = useWatch({ name: fieldName })

  const differentInternal = isUndefined(decimalScale)
    ? false
    : toNumber(value) !== round(toNumber(value), decimalScale)

  return (
    <FormControl
      label={formControlLabel}
      error={error?.message}
      tooltip={
        differentInternal
          ? {
              headline: 'Interner Wert weicht ab',
              text: 'Gespeicherte Dezimalstellen weichen von den angezeigten Werten ab. Wenn gespeichert wird, werden die angezeigten Werte übernommen und die Dezimalstellen abgeschnitten. Begrenzung abschalten um dies zu verhindern. Hinweis: Es kann möglicherweise trotzdem vorkommen, dass das Backend Werte rundet.',
            }
          : undefined
      }
    >
      <TextField
        {...textFieldProps}
        label={label}
        variant="outlined"
        onBlur={event => {
          onBlur()
          textFieldProps.onBlur?.(event)
        }}
        value={value}
        type="text"
        autoFocus={autoFocus}
        name={fieldName}
        disabled={disabled}
        multiline={multiline}
        rows={rows}
        inputRef={ref}
        size="small"
        color={differentInternal ? 'info' : undefined}
        sx={{
          ...(differentInternal
            ? {
                '& .MuiInputBase-input': {
                  color: 'info.main',
                },
              }
            : {}),
          ...textFieldProps.sx,
        }}
        slotProps={{
          input: {
            inputComponent: NumberInput as never,
            startAdornment: differentInternal ? (
              <InputAdornment position="start">
                <DecimalScaleWarningIndicator decimalScale={decimalScale as number} />
              </InputAdornment>
            ) : null,
            endAdornment: unit ? <InputAdornment position="end">{unit}</InputAdornment> : null,
            inputProps: {
              ...InputProps,
              decimalScale,
              decimalSeparator,
              disableDecimalScale: true,
              isNumericString: true,
              onValueChange: (values, sourceInfo) => {
                if (sourceInfo.source === 'event') {
                  const newValue = values.floatValue === undefined ? null : values.floatValue
                  onChange(newValue)
                  onValueChange?.(newValue)
                }
              },
            } as NumberFormatProps,
          },
        }}
      />
    </FormControl>
  )
}

export default CoordinateNumberInput
